// redux/slices/mlmTreeLevelSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

// -------------------- Async Thunks --------------------

export const fetchLevels = createAsyncThunk(
  "mlmTreeLevels/fetchLevels",
  async ({ companyId, serviceId, productId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (serviceId) params.append("service_id", serviceId);
      if (productId) params.append("product_id", productId);
      
      const res = await api.get(`/api/admin/mlm-levels/${companyId}?${params.toString()}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to fetch levels" }
      );
    }
  }
);

export const addLevel = createAsyncThunk(
  "mlmTreeLevels/addLevel",
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/admin/mlm-levels", data);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to add level" }
      );
    }
  }
);

export const updateLevel = createAsyncThunk(
  "mlmTreeLevels/updateLevel",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/api/admin/mlm-levels/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to update level" }
      );
    }
  }
);

export const deleteLevel = createAsyncThunk(
  "mlmTreeLevels/deleteLevel",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/api/admin/mlm-levels/${id}`);
      return { id, ...res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to delete level" }
      );
    }
  }
);

export const reorderLevels = createAsyncThunk(
  "mlmTreeLevels/reorderLevels",
  async ({ companyId, serviceId, productId, levels }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/admin/mlm-levels/reorder", {
        company_id: companyId,
        service_id: serviceId,
        product_id: productId,
        levels,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to reorder levels" }
      );
    }
  }
);

// Saves every level's percentage together so a live product stays at 100%.
export const saveLevelPercentages = createAsyncThunk(
  "mlmTreeLevels/saveLevelPercentages",
  async ({ companyId, serviceId, productId, levels }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/admin/mlm-levels/percentages", {
        company_id: companyId,
        service_id: serviceId,
        product_id: productId,
        levels,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to save percentages" }
      );
    }
  }
);

// -------------------- Slice --------------------

const mlmTreeLevelSlice = createSlice({
  name: "mlmTreeLevels",
  initialState: {
    levels: [],
    totalPercentage: 0,
    isValid: false,
    status: "idle",
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLevels: (state) => {
      state.levels = [];
      state.totalPercentage = 0;
      state.isValid = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchLevels.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchLevels.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.levels = action.payload.levels;
        state.totalPercentage = action.payload.total_percentage;
        state.isValid = action.payload.is_valid;
      })
      .addCase(fetchLevels.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Add
      .addCase(addLevel.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addLevel.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.levels.push(action.payload.data);
        state.levels.sort((a, b) => a.level_order - b.level_order);
        state.totalPercentage = action.payload.total_percentage;
        state.isValid = Math.abs(action.payload.total_percentage - 100) < 0.01;
      })
      .addCase(addLevel.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Update
      .addCase(updateLevel.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateLevel.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.levels.findIndex(
          (l) => l.id === action.payload.data.id
        );
        if (index >= 0) {
          state.levels[index] = action.payload.data;
        }
        state.levels.sort((a, b) => a.level_order - b.level_order);
        state.totalPercentage = action.payload.total_percentage;
        state.isValid = Math.abs(action.payload.total_percentage - 100) < 0.01;
      })
      .addCase(updateLevel.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Delete
      .addCase(deleteLevel.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteLevel.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.levels = state.levels.filter((l) => l.id !== action.payload.id);
        state.totalPercentage = action.payload.total_percentage;
        state.isValid = Math.abs(action.payload.total_percentage - 100) < 0.01;
      })
      .addCase(deleteLevel.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Reorder
      .addCase(reorderLevels.pending, (state) => {
        state.status = "loading";
      })
      .addCase(reorderLevels.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.levels = action.payload.levels;
      })
      .addCase(reorderLevels.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Save all percentages
      .addCase(saveLevelPercentages.pending, (state) => {
        state.status = "loading";
      })
      .addCase(saveLevelPercentages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.levels = action.payload.levels;
        state.totalPercentage = action.payload.total_percentage;
        state.isValid = action.payload.is_valid;
      })
      .addCase(saveLevelPercentages.rejected, (state) => {
        // Reason: the page shows this error itself, so don't also toast it via state.error.
        state.status = "failed";
      });
  },
});

export const { clearError, clearLevels } = mlmTreeLevelSlice.actions;
export default mlmTreeLevelSlice.reducer;
